import api from 'api/config';

/**
 * Conversations Service for managing Gigabot conversations, messages, threads, and shares
 * Provides methods to interact with the conversations API endpoints
 *
 * Backend endpoints:
 * - GET/POST /conversations - List/Create conversations
 * - GET/PUT/DELETE /conversations/:uuid - Single conversation operations
 * - POST /conversations/:uuid/archive|pin|star|branch - Conversation actions
 * - GET/POST /conversations/:uuid/messages - Message operations
 * - GET/POST /conversations/:uuid/attachments - Attachment operations
 * - GET/POST /conversations/:uuid/shares - Share operations
 * - GET /conversations/search - Search conversations
 * - GET /conversations/stats - User statistics
 * - GET/POST /threads - Thread operations
 * - GET /shared/:token - Public shared conversation access
 */
export const conversationsService = {
  // ========== CONVERSATIONS ==========

  /**
   * List conversations with pagination and filtering
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.perPage - Items per page (default: 20, max: 100)
   * @param {string} params.environment - Environment filter (profile, prompts, build, contracts, admin, global)
   * @param {string} params.contextType - Context type filter
   * @param {number} params.threadId - Thread ID filter
   * @param {boolean} params.includeArchived - Include archived conversations
   * @param {boolean} params.isPinned - Filter by pinned status
   * @param {boolean} params.isStarred - Filter by starred status
   * @param {string} params.q - Search term
   * @returns {Promise} Response with paginated conversations data
   */
  getConversations: async (params = {}) => {
    try {
      const queryParams = {
        page: params.page || 1,
        per_page: params.perPage || 20,
        ...(params.environment && { environment: params.environment }),
        ...(params.contextType && { context_type: params.contextType }),
        ...(params.threadId && { thread_id: params.threadId }),
        ...(params.includeArchived && { include_archived: 'true' }),
        ...(params.isPinned !== undefined && {
          is_pinned: params.isPinned.toString(),
        }),
        ...(params.isStarred !== undefined && {
          is_starred: params.isStarred.toString(),
        }),
        ...(params.q && { q: params.q }),
      };

      const response = await api.get('/conversations', { params: queryParams });
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get conversations:', error);
      return {
        success: false,
        error:
          error.response?.data?.error || 'Failed to retrieve conversations',
      };
    }
  },

  /**
   * Get a single conversation by UUID
   * @param {string} uuid - Conversation UUID
   * @param {Object} options - Options
   * @param {boolean} options.includeMessages - Include messages in response
   * @param {number} options.messageLimit - Max messages to include (default: 100, max: 500)
   * @returns {Promise} Response with conversation data
   */
  getConversation: async (uuid, options = {}) => {
    try {
      if (!uuid) {
        throw new Error('Conversation UUID is required');
      }

      const queryParams = {
        ...(options.includeMessages && { include_messages: 'true' }),
        ...(options.messageLimit && { message_limit: options.messageLimit }),
      };

      const response = await api.get(`/conversations/${uuid}`, {
        params: queryParams,
      });
      return {
        success: true,
        data: response.data.data,
        messages: response.data.messages,
      };
    } catch (error) {
      console.error('Failed to get conversation:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve conversation',
      };
    }
  },

  /**
   * Create a new conversation
   * @param {Object} data - Conversation creation data
   * @param {string} data.contextType - Context type (required)
   * @param {string} data.environment - Environment (profile, prompts, build, contracts, admin, global)
   * @param {number} data.threadId - Thread ID to associate with
   * @param {string} data.title - Conversation title
   * @param {string} data.relatedEntityType - Related entity type
   * @param {number} data.relatedEntityId - Related entity ID
   * @param {string} data.relatedEntityUuid - Related entity UUID
   * @param {Object} data.sessionMetadata - Session metadata
   * @returns {Promise} Response with created conversation data
   */
  createConversation: async (data) => {
    try {
      if (!data.contextType) {
        throw new Error('Context type is required');
      }

      const payload = {
        context_type: data.contextType,
        ...(data.environment && { environment: data.environment }),
        ...(data.threadId && { thread_id: data.threadId }),
        ...(data.title && { title: data.title }),
        ...(data.relatedEntityType && {
          related_entity_type: data.relatedEntityType,
        }),
        ...(data.relatedEntityId && {
          related_entity_id: data.relatedEntityId,
        }),
        ...(data.relatedEntityUuid && {
          related_entity_uuid: data.relatedEntityUuid,
        }),
        ...(data.sessionMetadata && { session_metadata: data.sessionMetadata }),
      };

      const response = await api.post('/conversations', payload);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to create conversation:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create conversation',
      };
    }
  },

  /**
   * Update a conversation
   * @param {string} uuid - Conversation UUID
   * @param {Object} updates - Fields to update
   * @param {string} updates.title - New title
   * @param {string} updates.summary - New summary
   * @param {boolean} updates.isArchived - Archive status
   * @param {boolean} updates.isPinned - Pin status
   * @param {boolean} updates.isStarred - Star status
   * @param {string[]} updates.tags - Tags array
   * @param {string} updates.primaryModel - Primary model used
   * @param {boolean} updates.webSearchEnabled - Web search enabled
   * @returns {Promise} Response with updated conversation data
   */
  updateConversation: async (uuid, updates) => {
    try {
      if (!uuid) {
        throw new Error('Conversation UUID is required');
      }

      const payload = {
        ...(updates.title !== undefined && { title: updates.title }),
        ...(updates.summary !== undefined && { summary: updates.summary }),
        ...(updates.isArchived !== undefined && {
          is_archived: updates.isArchived,
        }),
        ...(updates.isPinned !== undefined && { is_pinned: updates.isPinned }),
        ...(updates.isStarred !== undefined && {
          is_starred: updates.isStarred,
        }),
        ...(updates.tags !== undefined && { tags: updates.tags }),
        ...(updates.primaryModel !== undefined && {
          primary_model: updates.primaryModel,
        }),
        ...(updates.webSearchEnabled !== undefined && {
          web_search_enabled: updates.webSearchEnabled,
        }),
      };

      const response = await api.put(`/conversations/${uuid}`, payload);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to update conversation:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update conversation',
      };
    }
  },

  /**
   * Delete a conversation (soft delete)
   * @param {string} uuid - Conversation UUID
   * @returns {Promise} Response with success status
   */
  deleteConversation: async (uuid) => {
    try {
      if (!uuid) {
        throw new Error('Conversation UUID is required');
      }

      await api.delete(`/conversations/${uuid}`);
      return {
        success: true,
        message: 'Conversation deleted successfully',
      };
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete conversation',
      };
    }
  },

  // ========== CONVERSATION ACTIONS ==========

  /**
   * Archive or unarchive a conversation
   * @param {string} uuid - Conversation UUID
   * @param {boolean} archive - Archive status (true to archive, false to unarchive)
   * @returns {Promise} Response with updated archive status
   */
  archiveConversation: async (uuid, archive = true) => {
    try {
      if (!uuid) {
        throw new Error('Conversation UUID is required');
      }

      const response = await api.post(`/conversations/${uuid}/archive`, {
        archive,
      });
      return {
        success: true,
        isArchived: response.data.is_archived,
      };
    } catch (error) {
      console.error('Failed to archive conversation:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to archive conversation',
      };
    }
  },

  /**
   * Pin or unpin a conversation
   * @param {string} uuid - Conversation UUID
   * @param {boolean} pin - Pin status (true to pin, false to unpin)
   * @returns {Promise} Response with updated pin status
   */
  pinConversation: async (uuid, pin = true) => {
    try {
      if (!uuid) {
        throw new Error('Conversation UUID is required');
      }

      const response = await api.post(`/conversations/${uuid}/pin`, { pin });
      return {
        success: true,
        isPinned: response.data.is_pinned,
      };
    } catch (error) {
      console.error('Failed to pin conversation:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to pin conversation',
      };
    }
  },

  /**
   * Star or unstar a conversation
   * @param {string} uuid - Conversation UUID
   * @param {boolean} star - Star status (true to star, false to unstar)
   * @returns {Promise} Response with updated star status
   */
  starConversation: async (uuid, star = true) => {
    try {
      if (!uuid) {
        throw new Error('Conversation UUID is required');
      }

      const response = await api.post(`/conversations/${uuid}/star`, { star });
      return {
        success: true,
        isStarred: response.data.is_starred,
      };
    } catch (error) {
      console.error('Failed to star conversation:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to star conversation',
      };
    }
  },

  /**
   * Branch a conversation from a specific message
   * @param {string} uuid - Conversation UUID
   * @param {string} fromMessageId - Message UUID to branch from
   * @param {string} newTitle - Title for the new branched conversation
   * @returns {Promise} Response with new branched conversation data
   */
  branchConversation: async (uuid, fromMessageId, newTitle = null) => {
    try {
      if (!uuid || !fromMessageId) {
        throw new Error('Conversation UUID and message ID are required');
      }

      const payload = {
        from_message_id: fromMessageId,
        ...(newTitle && { new_title: newTitle }),
      };

      const response = await api.post(`/conversations/${uuid}/branch`, payload);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to branch conversation:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to branch conversation',
      };
    }
  },

  // ========== MESSAGES ==========

  /**
   * List messages for a conversation
   * @param {string} conversationUuid - Conversation UUID
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.perPage - Items per page (default: 50, max: 200)
   * @param {string} params.role - Filter by role (user, assistant, system)
   * @param {number} params.fromSequence - Start from sequence number
   * @param {number} params.toSequence - End at sequence number
   * @param {boolean} params.includeDeleted - Include deleted messages
   * @returns {Promise} Response with paginated messages data
   */
  getMessages: async (conversationUuid, params = {}) => {
    try {
      if (!conversationUuid) {
        throw new Error('Conversation UUID is required');
      }

      const queryParams = {
        page: params.page || 1,
        per_page: params.perPage || 50,
        ...(params.role && { role: params.role }),
        ...(params.fromSequence && { from_sequence: params.fromSequence }),
        ...(params.toSequence && { to_sequence: params.toSequence }),
        ...(params.includeDeleted && { include_deleted: 'true' }),
      };

      const response = await api.get(
        `/conversations/${conversationUuid}/messages`,
        { params: queryParams }
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get messages:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve messages',
      };
    }
  },

  /**
   * Create a new message in a conversation
   * @param {string} conversationUuid - Conversation UUID
   * @param {Object} data - Message data
   * @param {string} data.role - Message role (user, assistant, system)
   * @param {string} data.content - Message content
   * @param {string} data.modelUsed - Model used for generation
   * @param {string} data.provider - LLM provider
   * @param {number} data.inputTokens - Input token count
   * @param {number} data.outputTokens - Output token count
   * @param {number} data.estimatedCostMicrodollars - Cost in microdollars
   * @param {boolean} data.isStreaming - Whether message is streaming
   * @param {Object} data.webSearchResults - Web search results
   * @param {Object} data.actionProposal - Action proposal data
   * @param {Object} data.structuredData - Structured data (SDUI)
   * @param {Object} data.contextData - Context data
   * @returns {Promise} Response with created message data
   */
  createMessage: async (conversationUuid, data) => {
    try {
      if (!conversationUuid) {
        throw new Error('Conversation UUID is required');
      }
      if (!data.role || !data.content) {
        throw new Error('Role and content are required');
      }

      const payload = {
        role: data.role,
        content: data.content,
        ...(data.modelUsed && { model_used: data.modelUsed }),
        ...(data.provider && { provider: data.provider }),
        ...(data.inputTokens && { input_tokens: data.inputTokens }),
        ...(data.outputTokens && { output_tokens: data.outputTokens }),
        ...(data.estimatedCostMicrodollars && {
          estimated_cost_microdollars: data.estimatedCostMicrodollars,
        }),
        ...(data.isStreaming !== undefined && {
          is_streaming: data.isStreaming,
        }),
        ...(data.webSearchResults && {
          web_search_results: data.webSearchResults,
        }),
        ...(data.actionProposal && { action_proposal: data.actionProposal }),
        ...(data.structuredData && { structured_data: data.structuredData }),
        ...(data.contextData && { context_data: data.contextData }),
      };

      const response = await api.post(
        `/conversations/${conversationUuid}/messages`,
        payload
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to create message:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create message',
      };
    }
  },

  /**
   * Update a message
   * @param {string} conversationUuid - Conversation UUID
   * @param {string} messageUuid - Message UUID
   * @param {Object} updates - Fields to update
   * @returns {Promise} Response with updated message data
   */
  updateMessage: async (conversationUuid, messageUuid, updates) => {
    try {
      if (!conversationUuid || !messageUuid) {
        throw new Error('Conversation UUID and message UUID are required');
      }

      const response = await api.put(
        `/conversations/${conversationUuid}/messages/${messageUuid}`,
        updates
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to update message:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update message',
      };
    }
  },

  /**
   * Delete a message (soft delete)
   * @param {string} conversationUuid - Conversation UUID
   * @param {string} messageUuid - Message UUID
   * @returns {Promise} Response with success status
   */
  deleteMessage: async (conversationUuid, messageUuid) => {
    try {
      if (!conversationUuid || !messageUuid) {
        throw new Error('Conversation UUID and message UUID are required');
      }

      await api.delete(
        `/conversations/${conversationUuid}/messages/${messageUuid}`
      );
      return {
        success: true,
        message: 'Message deleted successfully',
      };
    } catch (error) {
      console.error('Failed to delete message:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete message',
      };
    }
  },

  /**
   * Complete a streaming message
   * @param {string} conversationUuid - Conversation UUID
   * @param {string} messageUuid - Message UUID
   * @param {Object} data - Completion data
   * @param {string} data.finalContent - Final message content
   * @param {number} data.inputTokens - Input token count
   * @param {number} data.outputTokens - Output token count
   * @param {number} data.costMicrodollars - Cost in microdollars
   * @returns {Promise} Response with completed message data
   */
  completeStreamingMessage: async (conversationUuid, messageUuid, data) => {
    try {
      if (!conversationUuid || !messageUuid) {
        throw new Error('Conversation UUID and message UUID are required');
      }
      if (!data.finalContent) {
        throw new Error('Final content is required');
      }

      const payload = {
        final_content: data.finalContent,
        ...(data.inputTokens && { input_tokens: data.inputTokens }),
        ...(data.outputTokens && { output_tokens: data.outputTokens }),
        ...(data.costMicrodollars && {
          cost_microdollars: data.costMicrodollars,
        }),
      };

      const response = await api.post(
        `/conversations/${conversationUuid}/messages/${messageUuid}/complete`,
        payload
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to complete streaming message:', error);
      return {
        success: false,
        error:
          error.response?.data?.error || 'Failed to complete streaming message',
      };
    }
  },

  // ========== ATTACHMENTS ==========

  /**
   * List attachments for a conversation
   * @param {string} conversationUuid - Conversation UUID
   * @param {string} messageId - Optional message UUID to filter by
   * @returns {Promise} Response with attachments data
   */
  getAttachments: async (conversationUuid, messageId = null) => {
    try {
      if (!conversationUuid) {
        throw new Error('Conversation UUID is required');
      }

      const queryParams = messageId ? { message_id: messageId } : {};
      const response = await api.get(
        `/conversations/${conversationUuid}/attachments`,
        { params: queryParams }
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get attachments:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve attachments',
      };
    }
  },

  /**
   * Create an attachment
   * @param {string} conversationUuid - Conversation UUID
   * @param {Object} data - Attachment data
   * @param {string} data.messageId - Message UUID to attach to
   * @param {string} data.fileName - File name
   * @param {string} data.fileType - MIME type
   * @param {number} data.fileSize - File size in bytes
   * @param {string} data.storagePath - Storage path
   * @param {string} data.storageProvider - Storage provider (s3, local, etc.)
   * @param {number} data.width - Image width (optional)
   * @param {number} data.height - Image height (optional)
   * @returns {Promise} Response with created attachment data
   */
  createAttachment: async (conversationUuid, data) => {
    try {
      if (!conversationUuid) {
        throw new Error('Conversation UUID is required');
      }
      if (
        !data.fileName ||
        !data.fileType ||
        !data.fileSize ||
        !data.storagePath ||
        !data.storageProvider
      ) {
        throw new Error(
          'File name, type, size, storage path, and provider are required'
        );
      }

      const payload = {
        file_name: data.fileName,
        file_type: data.fileType,
        file_size: data.fileSize,
        storage_path: data.storagePath,
        storage_provider: data.storageProvider,
        ...(data.messageId && { message_id: data.messageId }),
        ...(data.width && { width: data.width }),
        ...(data.height && { height: data.height }),
      };

      const response = await api.post(
        `/conversations/${conversationUuid}/attachments`,
        payload
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to create attachment:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create attachment',
      };
    }
  },

  /**
   * Delete an attachment
   * @param {string} conversationUuid - Conversation UUID
   * @param {string} attachmentUuid - Attachment UUID
   * @returns {Promise} Response with success status
   */
  deleteAttachment: async (conversationUuid, attachmentUuid) => {
    try {
      if (!conversationUuid || !attachmentUuid) {
        throw new Error('Conversation UUID and attachment UUID are required');
      }

      await api.delete(
        `/conversations/${conversationUuid}/attachments/${attachmentUuid}`
      );
      return {
        success: true,
        message: 'Attachment deleted successfully',
      };
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete attachment',
      };
    }
  },

  // ========== SHARES ==========

  /**
   * List shares for a conversation
   * @param {string} conversationUuid - Conversation UUID
   * @param {Object} params - Query parameters
   * @param {boolean} params.includeExpired - Include expired shares
   * @param {boolean} params.includeRevoked - Include revoked shares
   * @returns {Promise} Response with shares data
   */
  getShares: async (conversationUuid, params = {}) => {
    try {
      if (!conversationUuid) {
        throw new Error('Conversation UUID is required');
      }

      const queryParams = {
        ...(params.includeExpired && { include_expired: 'true' }),
        ...(params.includeRevoked && { include_revoked: 'true' }),
      };

      const response = await api.get(
        `/conversations/${conversationUuid}/shares`,
        { params: queryParams }
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get shares:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve shares',
      };
    }
  },

  /**
   * Create a share for a conversation
   * @param {string} conversationUuid - Conversation UUID
   * @param {Object} data - Share data
   * @param {string} data.shareType - Share type (link, user, team, business)
   * @param {number} data.sharedWithUserId - User ID to share with
   * @param {number} data.sharedWithBusinessId - Business ID to share with
   * @param {string} data.accessLevel - Access level (read, comment, edit)
   * @param {string} data.expiresAt - Expiration date (ISO string)
   * @returns {Promise} Response with created share data
   */
  createShare: async (conversationUuid, data) => {
    try {
      if (!conversationUuid) {
        throw new Error('Conversation UUID is required');
      }
      if (!data.shareType) {
        throw new Error('Share type is required');
      }

      const payload = {
        share_type: data.shareType,
        ...(data.sharedWithUserId && {
          shared_with_user_id: data.sharedWithUserId,
        }),
        ...(data.sharedWithBusinessId && {
          shared_with_business_id: data.sharedWithBusinessId,
        }),
        ...(data.accessLevel && { access_level: data.accessLevel }),
        ...(data.expiresAt && { expires_at: data.expiresAt }),
      };

      const response = await api.post(
        `/conversations/${conversationUuid}/shares`,
        payload
      );
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to create share:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create share',
      };
    }
  },

  /**
   * Revoke a share
   * @param {string} conversationUuid - Conversation UUID
   * @param {string} shareUuid - Share UUID
   * @returns {Promise} Response with success status
   */
  revokeShare: async (conversationUuid, shareUuid) => {
    try {
      if (!conversationUuid || !shareUuid) {
        throw new Error('Conversation UUID and share UUID are required');
      }

      await api.delete(
        `/conversations/${conversationUuid}/shares/${shareUuid}`
      );
      return {
        success: true,
        message: 'Share revoked successfully',
      };
    } catch (error) {
      console.error('Failed to revoke share:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to revoke share',
      };
    }
  },

  /**
   * Get a shared conversation by token (public access, no auth required)
   * @param {string} token - Share token
   * @returns {Promise} Response with shared conversation data
   */
  getSharedConversation: async (token) => {
    try {
      if (!token) {
        throw new Error('Share token is required');
      }

      const response = await api.get(`/shared/${token}`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get shared conversation:', error);
      return {
        success: false,
        error:
          error.response?.data?.error ||
          'Failed to retrieve shared conversation',
      };
    }
  },

  // ========== SEARCH ==========

  /**
   * Search conversations
   * @param {string} query - Search query
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.perPage - Items per page (default: 20, max: 100)
   * @param {string} params.environment - Environment filter
   * @param {boolean} params.includeArchived - Include archived conversations
   * @returns {Promise} Response with search results
   */
  searchConversations: async (query, params = {}) => {
    try {
      if (!query) {
        throw new Error('Search query is required');
      }

      const queryParams = {
        q: query,
        page: params.page || 1,
        per_page: params.perPage || 20,
        ...(params.environment && { environment: params.environment }),
        ...(params.includeArchived && { include_archived: 'true' }),
      };

      const response = await api.get('/conversations/search', {
        params: queryParams,
      });
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to search conversations:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Search failed',
      };
    }
  },

  /**
   * Search messages
   * @param {string} query - Search query
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.perPage - Items per page (default: 20, max: 100)
   * @param {string} params.conversationId - Filter by conversation UUID
   * @returns {Promise} Response with search results
   */
  searchMessages: async (query, params = {}) => {
    try {
      if (!query) {
        throw new Error('Search query is required');
      }

      const queryParams = {
        q: query,
        page: params.page || 1,
        per_page: params.perPage || 20,
        ...(params.conversationId && {
          conversation_id: params.conversationId,
        }),
      };

      const response = await api.get('/conversations/search/messages', {
        params: queryParams,
      });
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to search messages:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Search failed',
      };
    }
  },

  // ========== STATS ==========

  /**
   * Get user conversation statistics
   * @param {string} environment - Optional environment filter
   * @returns {Promise} Response with statistics data
   */
  getStats: async (environment = null) => {
    try {
      const queryParams = environment ? { environment } : {};
      const response = await api.get('/conversations/stats', {
        params: queryParams,
      });
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get stats:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve statistics',
      };
    }
  },

  // ========== THREADS ==========

  /**
   * List threads
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.perPage - Items per page (default: 20, max: 100)
   * @param {string} params.environment - Environment filter
   * @param {string} params.contextType - Context type filter
   * @param {boolean} params.includeArchived - Include archived threads
   * @param {boolean} params.isPinned - Filter by pinned status
   * @returns {Promise} Response with paginated threads data
   */
  getThreads: async (params = {}) => {
    try {
      const queryParams = {
        page: params.page || 1,
        per_page: params.perPage || 20,
        ...(params.environment && { environment: params.environment }),
        ...(params.contextType && { context_type: params.contextType }),
        ...(params.includeArchived && { include_archived: 'true' }),
        ...(params.isPinned !== undefined && {
          is_pinned: params.isPinned.toString(),
        }),
      };

      const response = await api.get('/threads', { params: queryParams });
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get threads:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve threads',
      };
    }
  },

  /**
   * Get a single thread by UUID
   * @param {string} uuid - Thread UUID
   * @returns {Promise} Response with thread data
   */
  getThread: async (uuid) => {
    try {
      if (!uuid) {
        throw new Error('Thread UUID is required');
      }

      const response = await api.get(`/threads/${uuid}`);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to get thread:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve thread',
      };
    }
  },

  /**
   * Create a new thread
   * @param {Object} data - Thread creation data
   * @param {string} data.contextType - Context type (required)
   * @param {string} data.title - Thread title
   * @param {string} data.description - Thread description
   * @param {string} data.environment - Environment
   * @param {Object} data.metadata - Thread metadata
   * @returns {Promise} Response with created thread data
   */
  createThread: async (data) => {
    try {
      if (!data.contextType) {
        throw new Error('Context type is required');
      }

      const payload = {
        context_type: data.contextType,
        ...(data.title && { title: data.title }),
        ...(data.description && { description: data.description }),
        ...(data.environment && { environment: data.environment }),
        ...(data.metadata && { metadata: data.metadata }),
      };

      const response = await api.post('/threads', payload);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to create thread:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create thread',
      };
    }
  },

  /**
   * Update a thread
   * @param {string} uuid - Thread UUID
   * @param {Object} updates - Fields to update
   * @param {string} updates.title - New title
   * @param {string} updates.description - New description
   * @param {boolean} updates.isPinned - Pin status
   * @param {boolean} updates.isArchived - Archive status
   * @param {Object} updates.metadata - Thread metadata
   * @returns {Promise} Response with updated thread data
   */
  updateThread: async (uuid, updates) => {
    try {
      if (!uuid) {
        throw new Error('Thread UUID is required');
      }

      const payload = {
        ...(updates.title !== undefined && { title: updates.title }),
        ...(updates.description !== undefined && {
          description: updates.description,
        }),
        ...(updates.isPinned !== undefined && { is_pinned: updates.isPinned }),
        ...(updates.isArchived !== undefined && {
          is_archived: updates.isArchived,
        }),
        ...(updates.metadata !== undefined && { metadata: updates.metadata }),
      };

      const response = await api.put(`/threads/${uuid}`, payload);
      return {
        success: true,
        data: response.data.data,
      };
    } catch (error) {
      console.error('Failed to update thread:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update thread',
      };
    }
  },

  /**
   * Delete a thread (soft delete)
   * @param {string} uuid - Thread UUID
   * @returns {Promise} Response with success status
   */
  deleteThread: async (uuid) => {
    try {
      if (!uuid) {
        throw new Error('Thread UUID is required');
      }

      await api.delete(`/threads/${uuid}`);
      return {
        success: true,
        message: 'Thread deleted successfully',
      };
    } catch (error) {
      console.error('Failed to delete thread:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete thread',
      };
    }
  },
};

export default conversationsService;
